import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ThunkAction } from 'redux-thunk'
import { AnyAction } from 'redux'
import type { RootState } from 'redux/createStore'
import { moduleName } from './constants'
import { IVendorInfoState, IVendorInfoErrors } from './types'
import {
  getVendorInfo,
  updateVendorInfo as updateVendorInfoAPI
} from 'features/VendorInfo/api'
import { TAddress } from 'router/types'
import { setLoading } from '../common/slice'
import {
  VendorDetails,
  VendorUpdatePayload
} from '../../../features/MyCommunity/Vendors/VendorCreation/types'

const initialState: IVendorInfoState = {
  vendorInfo: {
    uuid: '',
    name: '',
    business_name: '',
    description: '',
    phone_number: '',
    address: {} as TAddress,
    vendor_permission: 'full_access',
    moov_business_account: null
  },
  error: null
}

const VendorInfoSlice = createSlice({
  name: moduleName,
  initialState,
  reducers: {
    setVendorInfoError(
      state: IVendorInfoState,
      action: PayloadAction<{ error: IVendorInfoErrors | null }>
    ) {
      const { error } = action.payload
      state.error = error
    },
    setVendorInfoAction(
      state: IVendorInfoState,
      action: PayloadAction<{ vendorInfo: VendorDetails }>
    ) {
      const { vendorInfo } = action.payload
      state.vendorInfo = vendorInfo
    },
    updateVendorInfoAction(
      state: IVendorInfoState,
      action: PayloadAction<{ updatedVendorInfo: VendorDetails }>
    ) {
      const { updatedVendorInfo } = action.payload
      state.vendorInfo = {
        ...(state.vendorInfo || {}),
        ...updatedVendorInfo
      }
    },
    resetVendorInfoAction(state: IVendorInfoState) {
      Object.keys(initialState).forEach(
        (key) =>
          ((state[key] as IVendorInfoState[typeof key]) = initialState[key])
      )
    }
  }
})

export default VendorInfoSlice.reducer
export const {
  setVendorInfoError,
  setVendorInfoAction,
  updateVendorInfoAction,
  resetVendorInfoAction
} = VendorInfoSlice.actions

export const fetchVendorInfo =
  (
    uuid: string,
    completedCallback?: (data: VendorDetails) => void
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true))
      const result = await getVendorInfo(uuid)
      if (result?.data) {
        const { alternate_name_set, ...rest } = result.data
        const vendorDetails: VendorDetails = {
          ...rest,
          alternate_name_set: alternate_name_set
            ? alternate_name_set.map((item) => ({ name: item }))
            : undefined
        }
        dispatch(
          setVendorInfoAction({
            vendorInfo: vendorDetails
          })
        )
        completedCallback?.(vendorDetails)
      }
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'data' in err) {
        dispatch(
          setVendorInfoError({
            error: (err as { data: IVendorInfoErrors }).data
          })
        )
      } else {
        dispatch(setVendorInfoError({ error: err as IVendorInfoErrors }))
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

export const updateVendorInfo =
  (
    data: VendorUpdatePayload,
    uuid: string,
    completedCallback?: (data: VendorDetails) => void
  ): ThunkAction<Promise<void>, RootState, unknown, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true))
      const result = await updateVendorInfoAPI(data, uuid)
      if (result) {
        dispatch(
          setVendorInfoAction({
            vendorInfo: {
              ...data,
              uuid,
              address: data.address || undefined
            }
          })
        )
        completedCallback?.(result.data)
      }
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'data' in err) {
        dispatch(
          setVendorInfoError({
            error: (err as { data: IVendorInfoErrors }).data
          })
        )
      } else {
        dispatch(setVendorInfoError({ error: err as IVendorInfoErrors }))
      }
    } finally {
      dispatch(setLoading(false))
    }
  }
